// Lean matcher — implements TDD §5 product intent in ~250 lines.
// Inputs: user's answers + the destination catalog.
// Output: ranked list of compatible destinations.

import type {
  AnswerEntry,
  Destination,
  Friendliness,
  SkillLevel,
  VibeTag,
} from "./types";
import { SKILL_RANK } from "./types";

export interface ScoredDestination {
  destination: Destination;
  score: number;
}

interface UserProfile {
  statedSkill: SkillLevel;
  skillConfidence: number; // 0..2; right-answer scenarios bump this
  vibePreference: VibeTag | null;
  warmth: number; // 0..4 (slider position)
  budgetTier: "low" | "mid" | "high" | "top" | null;
  daysAvailable: number; // approx min of the band the user picked
  englishComfort: "fluent" | "ok" | "low" | null;
  cultureWant: "temple" | "bar" | "zen" | "shop" | null;
  currentTolerance: "low" | "mid" | "high" | null;
  marineLife: Set<string>;             // sharks / mantas / whale-shark / dolphins / turtles / hammerheads
  liveaboardTolerance: "must" | "depend" | "no" | null;
  photographyMode: "dslr" | "phone" | "nothing" | "pointer" | null;
  firstDiveConcern: "depth" | "gear" | "balance" | "nothing" | null;
  beenThere: Set<string>;
}

export function buildProfile(answers: AnswerEntry[]): UserProfile {
  const p: UserProfile = {
    statedSkill: "SKILL_NONE",
    skillConfidence: 0,
    vibePreference: null,
    warmth: 2,
    budgetTier: null,
    daysAvailable: 7,
    englishComfort: null,
    cultureWant: null,
    currentTolerance: null,
    marineLife: new Set(),
    liveaboardTolerance: null,
    photographyMode: null,
    firstDiveConcern: null,
    beenThere: new Set(),
  };

  for (const a of answers) {
    switch (a.questionId) {
      case "QUESTION_S1":
        if (a.chosenValue === "cert-veteran") p.statedSkill = "SKILL_ADVANCED_AOW";
        else if (a.chosenValue === "cert-pro") p.statedSkill = "SKILL_AOW";
        else if (a.chosenValue === "cert-ow") p.statedSkill = "SKILL_OW";
        else p.statedSkill = "SKILL_NONE";
        break;

      case "QUESTION_V1":
      case "QUESTION_V2":
      case "QUESTION_V3":
        if (a.chosenValue === "bigfish") p.vibePreference = "BIG_ANIMALS";
        else if (a.chosenValue === "macro") p.vibePreference = "MACRO";
        else if (a.chosenValue === "wreck") p.vibePreference = "WRECK";
        else if (a.chosenValue === "resort") p.vibePreference = "RESORT";
        break;

      case "QUESTION_T1":
        if (a.chosenValue === "drysuit") p.warmth = 0;
        else if (a.chosenValue === "5mm") p.warmth = 1;
        else if (a.chosenValue === "3mm") p.warmth = 3;
        else if (a.chosenValue === "bikini") p.warmth = 4;
        break;
      case "QUESTION_T2":
        p.warmth = parseInt(a.chosenValue, 10);
        if (!Number.isFinite(p.warmth)) p.warmth = 2;
        break;

      case "QUESTION_B1":
      case "QUESTION_B2":
        p.budgetTier = a.chosenValue as UserProfile["budgetTier"];
        break;

      case "QUESTION_D1":
      case "QUESTION_D2":
        if (a.chosenValue === "3-5") p.daysAvailable = 5;
        else if (a.chosenValue === "5-8") p.daysAvailable = 8;
        else if (a.chosenValue === "8-14") p.daysAvailable = 14;
        else if (a.chosenValue === "15+") p.daysAvailable = 21;
        break;

      case "QUESTION_LG1":
        p.englishComfort = a.chosenValue as UserProfile["englishComfort"];
        break;
      case "QUESTION_LG2":
        // LG2 has scenario-shaped values; map back to comfort tiers.
        if (a.chosenValue === "english") p.englishComfort = "fluent";
        else if (a.chosenValue === "chinese") p.englishComfort = "low";
        else p.englishComfort = "ok"; // gesture / translate
        break;

      case "QUESTION_CL1":
      case "QUESTION_CL2":
        if (a.chosenValue !== "diveonly")
          p.cultureWant = a.chosenValue as UserProfile["cultureWant"];
        break;

      case "QUESTION_C1":
        p.currentTolerance = a.chosenValue as UserProfile["currentTolerance"];
        break;
      case "QUESTION_C2":
        // Scenario: "drift" = +1 confidence AND signals high tolerance.
        if (a.chosenValue === "drift") {
          p.currentTolerance = "high";
          p.skillConfidence++;
        } else if (a.chosenValue === "kick") p.currentTolerance = "mid";
        else if (a.chosenValue === "ascend") p.currentTolerance = "low";
        else if (a.chosenValue === "hold") p.currentTolerance = "low";
        break;

      case "QUESTION_M1":
        a.chosenValue.split(",").filter(Boolean).forEach((v) => p.marineLife.add(v));
        break;

      case "QUESTION_M2":
        p.marineLife.add(a.chosenValue);
        break;

      case "QUESTION_L1":
      case "QUESTION_L2":
        p.liveaboardTolerance = a.chosenValue as UserProfile["liveaboardTolerance"];
        break;

      case "QUESTION_P1":
      case "QUESTION_P2":
        p.photographyMode = a.chosenValue as UserProfile["photographyMode"];
        break;

      case "QUESTION_F1":
        p.firstDiveConcern = a.chosenValue as UserProfile["firstDiveConcern"];
        break;

      case "QUESTION_BT1":
        a.chosenValue.split(",").filter(Boolean).forEach((id) => p.beenThere.add(id));
        break;

      // Skill-confidence (right answer = +1, never penalized otherwise)
      case "QUESTION_S2":
        if (a.chosenValue === "option_1") p.skillConfidence++;
        break;
      case "QUESTION_S3":
        if (a.chosenValue === "stonefish") p.skillConfidence++;
        break;
      case "QUESTION_S4":
        if (a.chosenValue === "mantis-shrimp" || a.chosenValue === "frogfish") p.skillConfidence++;
        break;
      case "QUESTION_S5":
        if (a.chosenValue === "easy") p.skillConfidence++;
        break;

      default:
        break;
    }
  }

  // Cap confidence per PRD §5.5
  if (p.skillConfidence > 2) p.skillConfidence = 2;

  return p;
}

const BUDGET_MAX_RMB: Record<NonNullable<UserProfile["budgetTier"]>, number> = {
  low: 5000,
  mid: 15000,
  high: 30000,
  top: 100000,
};

const FRIENDLY_BONUS: Record<Friendliness, number> = {
  FRIENDLY_HIGH: 0.4,
  FRIENDLY_MEDIUM: 0.2,
  FRIENDLY_LOW: 0,
};

// Marine-life pick (e.g. "hammerheads") → tags expected in destination's
// `marine_life` array. Most map directly; a couple need synonym handling.
const MARINE_LIFE_SYNONYMS: Record<string, string[]> = {
  sharks: ["sharks", "reef-sharks", "tiger-sharks", "thresher-sharks", "whitetip-sharks"],
  mantas: ["mantas", "manta"],
  "whale-shark": ["whale-shark"],
  dolphins: ["dolphins"],
  turtles: ["turtles"],
  hammerheads: ["hammerheads"],
};

export function rankDestinations(
  catalog: Destination[],
  profile: UserProfile,
  excludeIds: Set<string> = new Set()
): ScoredDestination[] {
  const effectiveSkill: SkillLevel =
    profile.skillConfidence >= 2
      ? bumpSkill(profile.statedSkill)
      : profile.statedSkill;

  const compatible: ScoredDestination[] = [];

  for (const d of catalog) {
    if (excludeIds.has(d.id)) continue;
    if (profile.beenThere.has(d.id)) continue;

    // ---- Hard filters (use STATED skill, not effective) ----
    if (SKILL_RANK[d.skill_min] > SKILL_RANK[profile.statedSkill]) continue;
    if (d.min_days > profile.daysAvailable) continue;
    if (d.english_required === "EN_FLUENT" && profile.englishComfort === "low") continue;
    if (profile.budgetTier && BUDGET_MAX_RMB[profile.budgetTier] * 1.4 < d.budget_rmb_pp.min) continue;
    if (
      d.current_strength === "CURRENT_HIGH" &&
      (profile.statedSkill === "SKILL_NONE" || profile.vibePreference === "RESORT")
    )
      continue;
    // Liveaboard hard avoid: user said "no" → drop liveaboard-only destinations
    // (those that have LIVEABOARD vibe AND no resort/reef alternative).
    if (
      profile.liveaboardTolerance === "no" &&
      d.vibe_tags.includes("LIVEABOARD") &&
      !d.vibe_tags.includes("RESORT") &&
      !d.vibe_tags.includes("REEF")
    ) {
      continue;
    }

    // ---- Soft scoring ----
    let score = 0;

    // Vibe match
    if (profile.vibePreference && d.vibe_tags.includes(profile.vibePreference)) score += 1.5;

    // Warmth alignment
    const avgTemp = (d.water_temp_c.min + d.water_temp_c.max) / 2;
    score += 0.6 * scoreWarmth(profile.warmth, avgTemp);

    // Chinese-friendly bonus, doubled if user said low English
    let friendly = FRIENDLY_BONUS[d.chinese_friendly];
    if (profile.englishComfort === "low") friendly *= 2;
    score += friendly;

    // Skill tier alignment (uses effective skill — confidence helps here)
    score += 0.4 * skillTierAlignment(effectiveSkill, d.skill_min);

    // Days alignment (penalize wasting half the trip on travel)
    if (d.min_days * 1.5 < profile.daysAvailable) score += 0.1;

    // Current tolerance
    if (profile.currentTolerance) {
      const tRank = { low: 0, mid: 1, high: 2 }[profile.currentTolerance];
      const dRank = { CURRENT_LOW: 0, CURRENT_MIXED: 1, CURRENT_HIGH: 2 }[d.current_strength];
      score += 0.3 * (1 - Math.abs(tRank - dRank) / 2);
    }

    // Marine-life specificity: +0.5 per match, max +1
    if (profile.marineLife.size > 0) {
      let mlBoost = 0;
      for (const want of profile.marineLife) {
        const synonyms = MARINE_LIFE_SYNONYMS[want] ?? [want];
        if (synonyms.some((s) => d.marine_life.includes(s))) mlBoost += 0.5;
      }
      score += Math.min(mlBoost, 1);
    }

    // Liveaboard tolerance soft signal
    if (profile.liveaboardTolerance === "must" && d.vibe_tags.includes("LIVEABOARD")) {
      score += 0.7;
    } else if (profile.liveaboardTolerance === "depend" && d.vibe_tags.includes("LIVEABOARD")) {
      score += 0.2;
    }

    // Photography mode: pros want macro / good viz; nothing-folks want easy
    if (profile.photographyMode === "dslr" && d.vibe_tags.includes("MACRO")) score += 0.4;
    if (profile.photographyMode === "nothing" && d.vibe_tags.includes("RESORT")) score += 0.2;

    // First-dive concerns: any concern → boost FIRST_DIVE / RESORT, dock REMOTE
    if (profile.firstDiveConcern && profile.firstDiveConcern !== "nothing") {
      if (d.vibe_tags.includes("FIRST_DIVE") || d.vibe_tags.includes("RESORT")) score += 0.5;
      if (d.vibe_tags.includes("REMOTE")) score -= 0.3;
    }

    // Culture / land vibe: small nudges
    if (profile.cultureWant === "temple" && d.vibe_tags.includes("CULTURE")) score += 0.3;
    if (profile.cultureWant === "bar" && d.vibe_tags.includes("NIGHTLIFE")) score += 0.3;
    if (profile.cultureWant === "zen" && d.vibe_tags.includes("REMOTE")) score += 0.3;
    if (profile.cultureWant === "shop" && d.vibe_tags.includes("RESORT")) score += 0.2;

    compatible.push({ destination: d, score });
  }

  // Spotlight boost: 10% chance, +1 to one random lesser-known
  if (Math.random() < 0.1) {
    const lesser = compatible.filter((s) => s.destination.tier === "LESSER_KNOWN");
    if (lesser.length > 0) {
      const pick = lesser[Math.floor(Math.random() * lesser.length)];
      pick.score += 1;
    }
  }

  // Softmax tie-break among the top 3
  compatible.sort((a, b) => b.score - a.score);
  const top = compatible.slice(0, 3);
  if (top.length > 1) {
    const tau = 0.6;
    const expScores = top.map((s) => Math.exp(s.score / tau));
    const sumExp = expScores.reduce((a, b) => a + b, 0);
    const r = Math.random() * sumExp;
    let acc = 0;
    let chosenIdx = 0;
    for (let i = 0; i < expScores.length; i++) {
      acc += expScores[i];
      if (r <= acc) { chosenIdx = i; break; }
    }
    if (chosenIdx > 0) {
      const [chosen] = top.splice(chosenIdx, 1);
      top.unshift(chosen);
      compatible.splice(0, top.length, ...top);
    }
  }

  return compatible;
}

function bumpSkill(s: SkillLevel): SkillLevel {
  switch (s) {
    case "SKILL_NONE": return "SKILL_OW";
    case "SKILL_OW": return "SKILL_AOW";
    case "SKILL_AOW": return "SKILL_ADVANCED_AOW";
    case "SKILL_ADVANCED_AOW": return "SKILL_TECH";
    case "SKILL_TECH": return "SKILL_TECH";
  }
}

function scoreWarmth(userWarmth: number, destAvgTemp: number): number {
  const warmDestPref = userWarmth >= 3;
  const veryWarm = destAvgTemp >= 28;
  const cold = destAvgTemp < 22;
  if (warmDestPref && veryWarm) return 1;
  if (!warmDestPref && cold) return 1;
  if (warmDestPref && cold) return -2;
  if (userWarmth === 2) return 0.5;
  return 0.3;
}

function skillTierAlignment(effective: SkillLevel, required: SkillLevel): number {
  const diff = SKILL_RANK[effective] - SKILL_RANK[required];
  if (diff === 0) return 1.5;
  if (diff === 1) return 0.6;
  if (diff === 2) return 0;
  if (diff < 0) return -1;
  return 0;
}

export function pickWhyBullets(
  destination: Destination,
  profile: UserProfile,
  max = 3
): string[] {
  const bullets: string[] = [];
  if (profile.vibePreference && destination.why_zh[profile.vibePreference]) {
    bullets.push(destination.why_zh[profile.vibePreference]);
  }
  if (profile.englishComfort === "low" && destination.why_zh["FRIENDLY_HIGH"] && destination.chinese_friendly === "FRIENDLY_HIGH") {
    bullets.push(destination.why_zh["FRIENDLY_HIGH"]);
  }
  if (profile.currentTolerance === "high" && destination.why_zh["DRIFT"]) {
    bullets.push(destination.why_zh["DRIFT"]);
  }
  if (profile.liveaboardTolerance === "must" && destination.why_zh["LIVEABOARD"]) {
    bullets.push(destination.why_zh["LIVEABOARD"]);
  }
  if (profile.cultureWant === "temple" && destination.why_zh["CULTURE"]) {
    bullets.push(destination.why_zh["CULTURE"]);
  }
  if (bullets.length < max && destination.why_zh["DEFAULT"]) {
    bullets.push(destination.why_zh["DEFAULT"]);
  }
  return bullets.slice(0, max);
}
